import { matchesUrl } from "findem-helpers";
import type { ICurrentUser } from "../types/users";

const EMBED_AUTH_TIMEOUT_MS = 10 * 1000;
const EMBED_REQUEST_TIMEOUT_MS = 15 * 1000;

interface EmbedAuthMessage {
  type: 'findem:embed-auth';
  embed_code: string;
}

export type EmbedAuthResult = { status: 'authenticated'; user: ICurrentUser } | { status: 'redirecting' };

export const isEmbedAuthRequested = (): boolean =>
  typeof window !== 'undefined'
  && window.parent !== window
  && new URLSearchParams(window.location.search).get('embed_auth') === 'postmessage';

const getParentOrigin = (): string => {
  if (window.parent === window) {
    throw new Error('Embed authentication requires an iframe context.');
  }
  const configuredOrigin = new URLSearchParams(window.location.search).get('embed_parent_origin');
  if (!configuredOrigin) {
    throw new Error('Embed authentication requires a parent origin.');
  }
  const parent = new URL(configuredOrigin);
  if (parent.protocol !== 'https:' || parent.origin !== configuredOrigin) {
    throw new Error('Embed authentication requires an exact HTTPS parent origin.');
  }
  if (document.referrer && new URL(document.referrer).origin !== parent.origin) {
    throw new Error('Embed authentication parent origin does not match the referrer.');
  }
  return parent.origin;
};

const isEmbedAuthMessage = (value: unknown): value is EmbedAuthMessage => {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Partial<EmbedAuthMessage>;
  return message.type === 'findem:embed-auth'
    && typeof message.embed_code === 'string'
    && /^[a-f0-9]{64}$/.test(message.embed_code);
};

const waitForEmbedCode = (parentOrigin: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
    };
    function handleMessage(event: MessageEvent<unknown>): void {
      if (event.source !== window.parent || event.origin !== parentOrigin || !isEmbedAuthMessage(event.data)) return;
      cleanup();
      resolve(event.data.embed_code);
    }
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timed out waiting for the parent embed authentication code.'));
    }, EMBED_AUTH_TIMEOUT_MS);

    window.addEventListener('message', handleMessage);
    try {
      window.parent.postMessage({
        type: 'findem:embed-ready',
        iframe_origin: window.location.origin,
      }, parentOrigin);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });

const fetchAuthJson = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), EMBED_REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${matchesUrl}${path}`, {
      ...options,
      credentials: 'include',
      mode: 'cors',
      cache: 'no-store',
      redirect: 'error',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Embed authentication request failed with status ${response.status}.`);
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
  }
};

const clearEmbedAuthParameters = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete('embed_auth');
  url.searchParams.delete('embed_parent_origin');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

// Called once by the entry-point bootstrap before the application mounts.
export const bootstrapEmbedSession = async (): Promise<EmbedAuthResult> => {
  const parentOrigin = getParentOrigin();
  const embedCode = await waitForEmbedCode(parentOrigin);
  const response = await fetchAuthJson('/auth/servicenow/embed-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ embed_code: embedCode, parent_origin: parentOrigin }),
  });
  if (typeof response !== 'object' || response === null) throw new Error('Invalid embed session response.');
  const result = response as { authenticated?: unknown; mfa_required?: unknown; redirect_uri?: unknown };
  if (result.mfa_required === true) {
    if (typeof result.redirect_uri !== 'string') throw new Error('Missing MFA destination.');
    const redirect = new URL(result.redirect_uri);
    if (redirect.protocol !== 'https:' || redirect.username || redirect.password) throw new Error('Invalid MFA destination.');
    window.location.assign(redirect.href);
    return { status: 'redirecting' };
  }
  if (result.authenticated !== true || result.mfa_required !== false) {
    throw new Error('Embed session exchange did not authenticate the user.');
  }

  // A successful exchange does not guarantee that the browser accepted the cookie.
  const user = await fetchAuthJson('/api/user_info');
  if (typeof user !== 'object' || user === null || !('uid' in user) || typeof user.uid !== 'string' || !user.uid) {
    throw new Error('The browser could not establish an embedded session.');
  }
  clearEmbedAuthParameters();
  return { status: 'authenticated', user: user as ICurrentUser };
};
